import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Pencil, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { adminApi, type ApiPlan } from '../../api';
import { ENTITLEMENT_CATALOG, MODULE_LABELS, emptyFeatureItems, SMS_REMINDER_FEATURE_ID, SMS_INTEGRATION_FEATURE_ID, SMS_PLAN_ITEM_IDS, entitlementCatalogStats, planCapabilitySummary } from '../../lib/planEntitlements';
import { emptyPlanPricing, formatPlanPrice, getPricingOptions, hasValidPlanPricing } from '../../lib/planPricing';
import type { BillingTerm } from '../../api';
import { SectionHeader } from '../shared';

const MODULES = Object.keys(MODULE_LABELS);
const CATALOG_STATS = entitlementCatalogStats();

const DURATION_ROWS: { term: BillingTerm; label: string; hint: string }[] = [
  { term: 'monthly', label: '1 month', hint: 'Billed every month' },
  { term: 'halfyearly', label: '6 months', hint: 'Half-yearly lump sum' },
  { term: 'yearly', label: '1 year', hint: 'Annual lump sum' },
];

function emptyDraft(): Omit<ApiPlan, 'createdAt' | 'updatedAt'> {
  return {
    planId: '',
    name: '',
    description: '',
    priceInr: 0,
    billingPeriod: 'monthly',
    pricing: emptyPlanPricing(),
    features: Object.fromEntries(MODULES.map((m) => [m, false])),
    featureItems: emptyFeatureItems(),
    mediaStorageLimitMb: 500,
    databaseStorageLimitMb: 1024,
    maxLedgers: 1,
    storageLimitMb: 500,
    isActive: true,
    sortOrder: 0,
  };
}

export function PlansSection() {
  const [plans, setPlans] = useState<ApiPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<ApiPlan | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [draft, setDraft] = useState(emptyDraft());
  const [saving, setSaving] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.listPlans();
      setPlans(res.plans);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load plans.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleModule = (mod: string, on: boolean) => {
    setDraft((d) => {
      const features = { ...d.features, [mod]: on };
      const featureItems = { ...d.featureItems };
      if (!on) {
        // Clear checklist items for this module, but never wipe the dedicated SMS flags
        // (those are controlled only by the SMS payment reminders toggle).
        ENTITLEMENT_CATALOG.filter((g) => g.module === mod).forEach((g) =>
          g.items.forEach((i) => {
            if (!SMS_PLAN_ITEM_IDS.has(i.id)) featureItems[i.id] = false;
          })
        );
      }
      return { ...d, features, featureItems };
    });
  };

  const toggleSmsReminders = (on: boolean) => {
    setDraft((d) => ({
      ...d,
      features: {
        ...d.features,
        // SMS needs Customers + Settings modules for send + schedule UI.
        customers: on ? true : d.features.customers,
        settings: on ? true : d.features.settings,
      },
      featureItems: {
        ...d.featureItems,
        [SMS_REMINDER_FEATURE_ID]: on,
        [SMS_INTEGRATION_FEATURE_ID]: on,
      },
    }));
  };

  const smsRemindersEnabled = Boolean(draft.featureItems?.[SMS_REMINDER_FEATURE_ID]);

  const toggleItem = (itemId: string, on: boolean) => {
    setDraft((d) => ({ ...d, featureItems: { ...d.featureItems, [itemId]: on } }));
  };

  const selectAllInGroup = (groupKey: string, mod: string) => {
    const group = ENTITLEMENT_CATALOG.find((g) => `${g.module}-${g.label}` === groupKey);
    if (!group) return;
    setDraft((d) => {
      const featureItems = { ...d.featureItems };
      group.items.forEach((i) => { featureItems[i.id] = true; });
      return { ...d, features: { ...d.features, [mod]: true }, featureItems };
    });
  };

  const buildPayload = (d: typeof draft, isEdit: boolean) => {
    const smsOn = Boolean(d.featureItems?.[SMS_REMINDER_FEATURE_ID]);
    const features = {
      ...d.features,
      ...(smsOn ? { customers: true, settings: true } : {}),
    };
    const featureItems = {
      ...emptyFeatureItems(),
      ...d.featureItems,
      [SMS_REMINDER_FEATURE_ID]: smsOn,
      [SMS_INTEGRATION_FEATURE_ID]: smsOn,
    };
    return {
      ...(isEdit ? {} : { planId: d.planId.trim().toLowerCase() }),
      name: d.name.trim(),
      description: d.description.trim(),
      pricing: d.pricing,
      features,
      featureItems,
      mediaStorageLimitMb: Math.max(0, Number(d.mediaStorageLimitMb ?? d.storageLimitMb) || 500),
      databaseStorageLimitMb: Math.max(0, Number(d.databaseStorageLimitMb) || 1024),
      maxLedgers: Math.max(0, Number(d.maxLedgers) ?? 1),
      isActive: d.isActive,
      sortOrder: d.sortOrder,
    };
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      if (!draft.name.trim()) {
        setError('Plan name is required.');
        return;
      }
      if (!editing && !draft.planId.trim()) {
        setError('Plan ID is required.');
        return;
      }
      if (!editing && !/^[a-z0-9][a-z0-9_-]{1,47}$/.test(draft.planId.trim())) {
        setError('Plan ID must be 2–48 lowercase letters, numbers, hyphens, or underscores.');
        return;
      }
      if (!hasValidPlanPricing(draft.pricing)) {
        setError('Enable at least one duration (1 month, 6 months, or 1 year) and set a price greater than zero.');
        return;
      }
      if (editing) {
        await adminApi.updatePlan(editing.planId, buildPayload(draft, true));
      } else {
        await adminApi.createPlan(buildPayload(draft, false) as Omit<ApiPlan, 'createdAt' | 'updatedAt'>);
      }
      setEditing(null);
      setDraft(emptyDraft());
      setShowForm(false);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save plan.');
    } finally {
      setSaving(false);
    }
  };

  const activePlans = useMemo(() => plans.filter((p) => p.isActive), [plans]);

  const removePlan = async (plan: ApiPlan) => {
    const msg =
      'Permanently delete this plan? It will be removed from the database. Plans assigned to tenants or active trials cannot be deleted.';
    if (!window.confirm(msg)) return;
    setError('');
    try {
      await adminApi.deletePlan(plan.planId);
      if (editing?.planId === plan.planId) {
        setEditing(null);
        setDraft(emptyDraft());
        setShowForm(false);
      }
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not delete plan.');
    }
  };

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Subscription plans"
        subtitle="Define price tiers, module access, and granular capabilities — collections, ERP, WhatsApp share, multi-ledger, SMS, and more."
        actions={
          <button type="button" onClick={() => { setEditing(null); setDraft(emptyDraft()); setShowForm(true); }} className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700">
            <Plus className="h-4 w-4" />
            New plan
          </button>
        }
      />

      {error ? <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-900">{error}</div> : null}

      {(showForm || editing) && (
        <div className="rounded-xl border border-zinc-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-zinc-900">{editing ? 'Edit plan' : 'Create plan'}</h3>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {!editing ? (
              <label className="block text-sm">
                <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Plan ID</span>
                <input value={draft.planId} onChange={(e) => setDraft((d) => ({ ...d, planId: e.target.value.toLowerCase() }))} className="mt-1 w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm" placeholder="starter" />
                <p className="mt-1 text-[10px] text-zinc-500">2–48 chars: lowercase letters, numbers, hyphens, underscores</p>
              </label>
            ) : null}
            <label className="block text-sm">
              <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Name</span>
              <input value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} className="mt-1 w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm" />
            </label>
            <label className="block text-sm sm:col-span-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Description</span>
              <input value={draft.description} onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))} className="mt-1 w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm" />
            </label>
          </div>

          <p className="mt-5 text-xs font-semibold uppercase tracking-wide text-zinc-500">Pricing by duration (INR)</p>
          <p className="mt-1 text-xs text-zinc-500">Enable each term and set the price tenants pay for that period. E.g. monthly ₹499, half-yearly ₹2,499, yearly ₹4,999.</p>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            {DURATION_ROWS.map((row) => {
              const tier = draft.pricing?.[row.term] || { priceInr: 0, enabled: row.term === 'monthly' };
              return (
                <div key={row.term} className="rounded-lg border border-zinc-200 bg-zinc-50/80 p-3">
                  <label className="flex items-center gap-2 text-sm font-semibold text-zinc-800">
                    <input
                      type="checkbox"
                      checked={Boolean(tier.enabled)}
                      onChange={(e) =>
                        setDraft((d) => ({
                          ...d,
                          pricing: {
                            ...emptyPlanPricing(),
                            ...d.pricing,
                            [row.term]: { ...tier, enabled: e.target.checked },
                          },
                        }))
                      }
                    />
                    {row.label}
                  </label>
                  <p className="mt-0.5 text-[10px] text-zinc-500">{row.hint}</p>
                  <input
                    type="number"
                    min={0}
                    disabled={!tier.enabled}
                    value={tier.priceInr}
                    onChange={(e) =>
                      setDraft((d) => ({
                        ...d,
                        pricing: {
                          ...emptyPlanPricing(),
                          ...d.pricing,
                          [row.term]: { ...tier, priceInr: Number(e.target.value) || 0 },
                        },
                      }))
                    }
                    className="mt-2 w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm disabled:bg-zinc-100"
                    placeholder="Price in ₹"
                  />
                </div>
              );
            })}
          </div>

          <p className="mt-5 text-xs font-semibold uppercase tracking-wide text-zinc-500">Storage & ledger limits per tenant</p>
          <div className="mt-3 grid gap-4 sm:grid-cols-3">
            <label className="block text-sm">
              <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Media storage (MB)</span>
              <input
                type="number"
                min={0}
                value={draft.mediaStorageLimitMb ?? draft.storageLimitMb ?? 500}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    mediaStorageLimitMb: Number(e.target.value) || 0,
                    storageLimitMb: Number(e.target.value) || 0,
                  }))
                }
                className="mt-1 w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm"
              />
              <p className="mt-1 text-[10px] text-zinc-500">Cloud uploads — logos, consultancy photos (Cloudinary).</p>
            </label>
            <label className="block text-sm">
              <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Database storage (MB)</span>
              <input
                type="number"
                min={0}
                value={draft.databaseStorageLimitMb ?? 1024}
                onChange={(e) => setDraft((d) => ({ ...d, databaseStorageLimitMb: Number(e.target.value) || 0 }))}
                className="mt-1 w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm"
              />
              <p className="mt-1 text-[10px] text-zinc-500">MongoDB — workspace data, finance & hand-loan records.</p>
            </label>
            <label className="block text-sm">
              <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Branch ledgers (max)</span>
              <input
                type="number"
                min={0}
                value={draft.maxLedgers ?? 1}
                onChange={(e) => setDraft((d) => ({ ...d, maxLedgers: Number(e.target.value) || 0 }))}
                className="mt-1 w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm"
              />
              <p className="mt-1 text-[10px] text-zinc-500">HQ default book counts toward this. Use 0 for unlimited branch books.</p>
            </label>
          </div>

          <div className="mt-5 rounded-xl border border-teal-200 bg-gradient-to-br from-teal-50/80 to-white p-4">
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                checked={smsRemindersEnabled}
                onChange={(e) => toggleSmsReminders(e.target.checked)}
                className="mt-1 rounded border-zinc-300"
              />
              <div>
                <p className="text-sm font-semibold text-zinc-900">SMS payment reminders</p>
                <p className="mt-1 text-xs leading-relaxed text-zinc-600">
                  Enables manual &amp; scheduled SMS payment reminders (Fast2SMS / MSG91) plus reminder-day settings.
                  When off, no SMS notifications are sent even if gateway keys are configured.
                </p>
              </div>
            </label>
          </div>

          <p className="mt-5 text-xs font-semibold uppercase tracking-wide text-zinc-500">Modules &amp; internal capabilities</p>
          <p className="mt-1 text-xs text-zinc-500">
            {CATALOG_STATS.moduleCount} modules · {CATALOG_STATS.itemCount} capabilities ({CATALOG_STATS.checklistCount} in checklist + {CATALOG_STATS.smsItemCount} via SMS toggle).
            Turn on a module first, then pick sub-capabilities. Leave all unchecked in a module to grant full module access (legacy behaviour).
          </p>
          <div className="mt-2 space-y-2">
            {MODULES.map((mod) => {
              const groups = ENTITLEMENT_CATALOG.filter((g) => g.module === mod);
              const capabilityCount = groups.reduce((n, g) => n + g.items.filter((i) => !SMS_PLAN_ITEM_IDS.has(i.id)).length, 0);
              return (
              <div key={mod} className="rounded-lg border border-zinc-100">
                <label className="flex cursor-pointer items-center gap-2 bg-zinc-50 px-3 py-2 text-sm font-semibold text-zinc-800">
                  <input type="checkbox" checked={Boolean(draft.features[mod])} onChange={(e) => toggleModule(mod, e.target.checked)} />
                  {MODULE_LABELS[mod]}
                  <span className="ml-auto text-[10px] font-normal text-zinc-500">
                    {groups.length} group{groups.length === 1 ? '' : 's'} · {capabilityCount} capabilities
                  </span>
                </label>
                {draft.features[mod] ? (
                  <div className="divide-y divide-zinc-50 px-2 py-1">
                    {groups.map((group) => {
                      const gKey = `${group.module}-${group.label}`;
                      const open = expandedGroups[gKey] ?? true;
                      return (
                        <div key={gKey} className="py-1">
                          <button type="button" onClick={() => setExpandedGroups((s) => ({ ...s, [gKey]: !open }))} className="flex w-full items-center gap-1 px-2 py-1 text-left text-xs font-semibold text-zinc-600 hover:text-zinc-900">
                            {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                            {group.label}
                            <span className="ml-auto text-[10px] font-normal text-emerald-700" onClick={(e) => { e.stopPropagation(); selectAllInGroup(gKey, mod); }}>Select all</span>
                          </button>
                          {open ? (
                            <div className="grid gap-1 pb-2 pl-6 sm:grid-cols-2">
                              {group.items.filter((item) => !SMS_PLAN_ITEM_IDS.has(item.id)).map((item) => (
                                <label key={item.id} className="flex items-center gap-2 rounded px-2 py-1 text-xs text-zinc-700 hover:bg-zinc-50">
                                  <input type="checkbox" checked={Boolean(draft.featureItems?.[item.id])} onChange={(e) => toggleItem(item.id, e.target.checked)} />
                                  {item.label}
                                </label>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            );
            })}
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <button type="button" onClick={() => { setEditing(null); setDraft(emptyDraft()); setShowForm(false); }} className="rounded-lg border border-zinc-200 px-4 py-2 text-sm font-semibold text-zinc-700">Cancel</button>
            <button type="button" disabled={saving} onClick={() => void save()} className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">{saving ? 'Saving…' : editing ? 'Update' : 'Create'}</button>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-zinc-200 bg-white shadow-sm">
        <div className="border-b border-zinc-100 px-4 py-3">
          <p className="text-sm font-semibold text-zinc-900">{activePlans.length} active plan{activePlans.length === 1 ? '' : 's'}</p>
        </div>
        {loading ? <p className="p-4 text-sm text-zinc-500">Loading…</p> : activePlans.length === 0 ? <p className="p-4 text-sm text-zinc-500">No plans yet.</p> : (
          <ul className="divide-y divide-zinc-100">
            {activePlans.map((plan) => {
              const summary = planCapabilitySummary(plan);
              return (
              <li key={plan.planId} className="flex flex-wrap items-start justify-between gap-3 px-4 py-4">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-zinc-900">{plan.name} <span className="text-xs text-zinc-400">({plan.planId})</span></p>
                  <p className="mt-1 text-xs text-zinc-500">
                    {getPricingOptions(plan).map((o) => `${o.label}: ${formatPlanPrice(o.priceInr)}`).join(' · ') || 'No pricing'}
                    {(plan.mediaStorageLimitMb ?? plan.storageLimitMb)
                      ? ` · Media ${plan.mediaStorageLimitMb ?? plan.storageLimitMb} MB`
                      : ''}
                    {plan.databaseStorageLimitMb ? ` · DB ${plan.databaseStorageLimitMb} MB` : ''}
                    {plan.maxLedgers != null
                      ? plan.maxLedgers > 0
                        ? ` · ${plan.maxLedgers} ledger${plan.maxLedgers === 1 ? '' : 's'}`
                        : ' · Unlimited ledgers'
                      : ''}
                  </p>
                  <p className="mt-1 text-xs font-medium text-zinc-700">
                    Modules: {summary.modules.length ? summary.modules.join(' · ') : 'None'}
                  </p>
                  {plan.featureItems?.[SMS_REMINDER_FEATURE_ID] ||
                  plan.featureItems?.['customers.whatsapp.reminders'] ? (
                    <p className="mt-1 text-xs font-medium text-teal-700">SMS reminders included</p>
                  ) : (
                    <p className="mt-1 text-xs text-zinc-400">No SMS reminders</p>
                  )}
                  {summary.capabilities.length > 0 ? (
                    <details className="mt-2">
                      <summary className="cursor-pointer text-xs font-semibold text-emerald-800">
                        {summary.capabilities.length} internal capabilities
                        {summary.hasGranular ? ' (granular)' : ' (full module access where enabled)'}
                      </summary>
                      <ul className="mt-2 max-h-48 space-y-1 overflow-y-auto rounded-lg border border-zinc-100 bg-zinc-50/80 p-2 text-[11px] text-zinc-600">
                        {summary.capabilities.map((c) => (
                          <li key={`${c.group}-${c.label}`}>
                            <span className="font-medium text-zinc-500">{c.group}:</span> {c.label}
                          </li>
                        ))}
                      </ul>
                    </details>
                  ) : null}
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={() => { setEditing(plan); setDraft({ ...plan, pricing: { ...emptyPlanPricing(), ...plan.pricing }, featureItems: { ...emptyFeatureItems(), ...plan.featureItems }, mediaStorageLimitMb: plan.mediaStorageLimitMb ?? plan.storageLimitMb ?? 500, databaseStorageLimitMb: plan.databaseStorageLimitMb ?? 1024, maxLedgers: plan.maxLedgers ?? 1, storageLimitMb: plan.mediaStorageLimitMb ?? plan.storageLimitMb ?? 500 }); setShowForm(true); }} className="rounded-lg border border-zinc-200 p-2"><Pencil className="h-4 w-4" /></button>
                  <button type="button" onClick={() => void removePlan(plan)} className="rounded-lg border border-rose-200 p-2 text-rose-700" title="Delete plan"><Trash2 className="h-4 w-4" /></button>
                </div>
              </li>
            );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
